// src/oura/delivery/AddAsExtraWork.tsx
//
// A client request that is more than the month's small change: staff add it
// to the plan as extra work, a custom line (X) on the contract with no checks
// behind it, so Delivery shows it under Needs us as one item staff tick when
// it is done. The contract edit is the same one "Add as a deliverable" on a
// site issue makes; saving the plan sends the client nothing.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getEngagement, updateEngagement, type Engagement } from "@service/aeo/clientReview";
import { useStaffSignature } from "../components/client/changeRequests";
import { cn } from "../lib/cn";
import { PillButton, TYPE } from "../ui";
import { OURA_ROOT } from "../nav";
import { FIELD } from "./drawerParts";

type Load = { state: "loading" } | { state: "none" } | { state: "failed" } | { state: "ready"; e: Engagement };

function isChangedRefusal(err: unknown): boolean {
    const res = (err as { response?: { status?: number; data?: { errors?: unknown[] } } }).response;
    return res?.status === 409 && Array.isArray(res.data?.errors) && res.data?.errors[0] === "engagement_changed";
}

function nextCustomCode(e: Engagement): string {
    const used = e.deliverables.map((d) => d.code.match(/^X(\d+)$/i)).filter(Boolean).map((m) => Number(m![1]));
    return `X${(used.length ? Math.max(...used) : 0) + 1}`;
}

/** The plan month today falls in, counted from the start; a first guess staff can change. */
function monthNow(e: Engagement): number {
    if (!e.startedAt) return 1;
    const start = new Date(e.startedAt);
    const now = new Date();
    const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()) - (now.getDate() < start.getDate() ? 1 : 0);
    return Math.min(Math.max(months + 1, 1), e.termMonths);
}

export function AddAsExtraWork({ businessId, title }: { businessId: string; title: string }) {
    const [load, setLoad] = useState<Load>({ state: "loading" });
    const [open, setOpen] = useState(false);
    const [label, setLabel] = useState(title);
    const [month, setMonth] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [added, setAdded] = useState<string | null>(null);
    const [reload, setReload] = useState(0);
    const staffName = useStaffSignature();

    useEffect(() => {
        let live = true;
        setLoad({ state: "loading" });
        getEngagement(businessId)
            .then((e) => live && setLoad(e ? { state: "ready", e } : { state: "none" }))
            .catch(() => live && setLoad({ state: "failed" }));
        return () => {
            live = false;
        };
    }, [businessId, reload]);

    if (load.state === "failed") {
        return (
            <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-600")}>
                The plan did not load, so extra work can’t be added yet.{" "}
                <PillButton variant="text" onClick={() => setReload((n) => n + 1)}>
                    Try again
                </PillButton>
            </p>
        );
    }
    // No contract: there is no plan to add the work to, so nothing is offered.
    if (load.state !== "ready") return null;
    const e = load.e;
    const code = nextCustomCode(e);

    const save = async () => {
        // Blank means this month; anything typed is taken as given, so 0 is refused, not swapped.
        const m = month.trim() === "" ? monthNow(e) : Number(month);
        if (!label.trim()) {
            setError("Give it a name the client will read.");
            return;
        }
        if (!Number.isInteger(m) || m < 1 || m > e.termMonths) {
            setError(`Pick a month from 1 to ${e.termMonths}.`);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const seeds = e.deliverables.map((d) => ({ ...d }));
            // No covers: one item, ticked by staff when the work is done.
            await updateEngagement(
                businessId,
                {
                    deliverables: [...seeds, { code, label: label.trim(), months: [m], quantity: null, cadence: "once", note: "Extra work you asked for", covers: [] }],
                    expectedUpdatedAt: e.updatedAt ?? null,
                },
                staffName ?? "Oura team",
                `Added to your plan: ${label.trim()}, month ${m}.`,
            );
            setAdded(`${code}-m${m}`);
            setOpen(false);
        } catch (err) {
            if (isChangedRefusal(err)) {
                const fresh = await getEngagement(businessId).catch(() => null);
                if (fresh) setLoad({ state: "ready", e: fresh });
                setError("Someone changed the plan a moment ago. It is reloaded now: add it again.");
            } else setError("The plan did not save. Try again.");
        } finally {
            setBusy(false);
        }
    };

    if (added) {
        return (
            <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-600")}>
                Added to the plan as {added.split("-m")[0]}.{" "}
                <Link className="font-medium text-blue-700 hover:underline" to={`${OURA_ROOT}/businesses/${encodeURIComponent(businessId)}/delivery?d=${encodeURIComponent(added)}`}>
                    Open it in Delivery
                </Link>
            </p>
        );
    }
    if (!open) {
        return (
            <p className={cn(TYPE.tc, "m-0 mt-1")}>
                <PillButton variant="text" writes onClick={() => setOpen(true)}>
                    Add as extra work
                </PillButton>
            </p>
        );
    }
    return (
        <form
            className="mt-3 space-y-3 rounded-xl bg-gray-50 p-3"
            onSubmit={(ev) => {
                ev.preventDefault();
                void save();
            }}
        >
            <p className={cn(TYPE.tc, "m-0 text-gray-600")}>
                Extra work beyond the plan: a line, {code}, on Delivery. You tick it when it is done. The client is not emailed.
            </p>
            <label className="block">
                <span className={TYPE.lab}>What the client reads</span>
                <input className={cn(FIELD, "mt-1")} value={label} onChange={(ev) => setLabel(ev.target.value)} maxLength={120} autoFocus />
            </label>
            <label className="block">
                <span className={TYPE.lab}>Plan month</span>
                <input
                    className={cn(FIELD, "mt-1 w-28")}
                    value={month}
                    onChange={(ev) => setMonth(ev.target.value.replace(/[^0-9]/g, ""))}
                    inputMode="numeric"
                    placeholder={`${monthNow(e)}`}
                    aria-describedby="extra-month-hint"
                />
                <span id="extra-month-hint" className={cn(TYPE.tm, "mt-1 block text-gray-500")}>
                    Blank means this month, month {monthNow(e)} of {e.termMonths}.
                </span>
            </label>
            {error && (
                <p role="alert" className={cn(TYPE.tc, "m-0 text-rose-600")}>
                    {error}
                </p>
            )}
            <div className="flex gap-2">
                <PillButton type="submit" variant="primary" busy={busy} writes>
                    Add {code}
                </PillButton>
                <PillButton onClick={() => setOpen(false)} disabled={busy}>
                    Cancel
                </PillButton>
            </div>
        </form>
    );
}
